import { useEffect, useRef } from 'react';

/**
 * Custom hook for scroll reveal animations using Intersection Observer API
 * @param {Object} options - Configuration options for the intersection observer
 * @param {number} options.threshold - Percentage of element visibility to trigger animation (0-1)
 * @param {string} options.rootMargin - Margin around the root element
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once
 * @param {number} options.delay - Delay before adding active class (in ms)
 * @returns {Object} - Ref to attach to the element and manual trigger function
 */
export const useScrollReveal = (options = {}) => {
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true,
    delay = 0,
  } = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Check if Intersection Observer is supported
    if (!window.IntersectionObserver) {
      // Fallback for older browsers - immediately show element
      element.classList.add('active');
      return;
    }

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add delay if specified
            if (delay > 0) {
              setTimeout(() => {
                entry.target.classList.add('active');
              }, delay);
            } else {
              entry.target.classList.add('active');
            }

            // Stop observing if triggerOnce is true
            if (triggerOnce) {
              observerRef.current?.unobserve(entry.target);
            }
          } else if (!triggerOnce) {
            // Remove active class if element goes out of view and triggerOnce is false
            entry.target.classList.remove('active');
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    // Start observing
    observerRef.current.observe(element);

    // Cleanup function
    return () => {
      if (observerRef.current && element) {
        observerRef.current.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce, delay]);

  // Manual trigger function
  const triggerReveal = () => {
    if (elementRef.current) {
      elementRef.current.classList.add('active');
    }
  };

  // Reset function
  const resetReveal = () => {
    if (elementRef.current) {
      elementRef.current.classList.remove('active');
    }
  };

  return {
    ref: elementRef,
    triggerReveal,
    resetReveal,
  };
};

/**
 * Hook for revealing multiple elements with staggered animation
 * @param {Object} options - Configuration options
 * @param {number} options.staggerDelay - Delay between each element animation (in ms)
 * @returns {Function} - Function to get ref for each element
 */
export const useStaggeredReveal = (options = {}) => {
  const elementsRef = useRef([]);
  const observerRef = useRef(null);

  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true,
    staggerDelay = 100,
  } = options;

  useEffect(() => {
    if (!window.IntersectionObserver) {
      // Fallback for older browsers
      elementsRef.current.forEach((element) => {
        if (element) element.classList.add('active');
      });
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementIndex = elementsRef.current.indexOf(entry.target);
            
            setTimeout(() => {
              entry.target.classList.add('active');
            }, elementIndex * staggerDelay);

            if (triggerOnce) {
              observerRef.current?.unobserve(entry.target);
            }
          } else if (!triggerOnce) {
            entry.target.classList.remove('active');
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    // Observe all elements
    elementsRef.current.forEach((element) => {
      if (element) {
        observerRef.current?.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        elementsRef.current.forEach((element) => {
          if (element) {
            observerRef.current?.unobserve(element);
          }
        });
      }
    };
  }, [threshold, rootMargin, triggerOnce, staggerDelay]);

  // Function to get ref for each element
  const getElementRef = (index) => (element) => {
    elementsRef.current[index] = element;
  };

  return { getElementRef };
};

/**
 * Simple hook that automatically applies scroll reveal to an element
 * Just returns a ref - no configuration needed
 */
export const useAutoReveal = () => {
  return useScrollReveal({
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
    triggerOnce: true,
  });
};

export default useScrollReveal;